import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchFeaturedDemands } from '@/service/api/demand'
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore'
import logoImg from '@/assets/logo.png'
import HomeIcon from './HomeIcon'

/** 格式化预算展示文本 */
function formatBudgetDisplay(budgetDisplay?: string, budgetType?: string): string {
  if (!budgetDisplay || budgetDisplay.trim() === '') {
    return budgetType === 'NEGOTIABLE' ? '面议' : '面议'
  }
  if (budgetDisplay.includes('面议') || budgetType === 'NEGOTIABLE') {
    return '面议'
  }

  // 1. 匹配区间数字: "200000-400000 CNY" 或 "200000-400000"
  const rangeMatch = budgetDisplay.match(/^(\d+)\s*-\s*(\d+)\s*(CNY|[A-Z]{3})?(.*)$/i)
  if (rangeMatch) {
    const num1 = parseInt(rangeMatch[1], 10)
    const num2 = parseInt(rangeMatch[2], 10)
    const str1 = num1 >= 10000 ? `${(num1 / 10000).toFixed(num1 % 10000 === 0 ? 0 : 1)}万` : `${num1}`
    const str2 = num2 >= 10000 ? `${(num2 / 10000).toFixed(num2 % 10000 === 0 ? 0 : 1)}万` : `${num2}`
    const suffix = rangeMatch[4] ? rangeMatch[4].trim() : '元'
    return `${str1} - ${str2}${suffix.includes('元') ? suffix : '元' + suffix}`
  }

  // 2. 匹配单数字及单位: "550元/天" 或 "200000 CNY" 或 "550"
  const singleMatch = budgetDisplay.match(/^(\d+)\s*(CNY|[A-Z]{3})?(.*)$/i)
  if (singleMatch) {
    const num = parseInt(singleMatch[1], 10)
    const str = num >= 10000 ? `${(num / 10000).toFixed(num % 10000 === 0 ? 0 : 1)}万` : `${num}`
    const rawSuffix = singleMatch[3] ? singleMatch[3].trim() : ''
    let suffix = '元'
    if (rawSuffix) {
      suffix = rawSuffix.includes('元') ? rawSuffix : `元${rawSuffix}`
    }
    return `${str}${suffix}`
  }

  return budgetDisplay.replace(/\bCNY\b/gi, '元').trim()
}

/** 渲染大号数字+单位的预算节点 */
function renderBudgetContent(budgetDisplay?: string, budgetType?: string) {
  const formatted = formatBudgetDisplay(budgetDisplay, budgetType) || '550元/天'

  if (formatted.includes('面议')) {
    return <span className="text-xl font-bold text-emerald-600">面议</span>
  }

  // 匹配前面的数字/区间和后面的单位文字
  const match = formatted.match(/^([0-9\.\-\s万]+)(.*)$/)
  if (match) {
    const numPart = match[1].trim()
    const unitPart = match[2].trim()
    return (
      <div className="flex items-baseline font-sans">
        <span className="text-2xl sm:text-[1.7rem] font-bold text-emerald-600 tracking-tight leading-none">
          {numPart}
        </span>
        {unitPart && (
          <span className="text-xs sm:text-sm font-semibold text-emerald-600/90 ml-0.5">
            {unitPart}
          </span>
        )}
      </div>
    )
  }

  return <span className="text-xl font-bold text-emerald-600">{formatted}</span>
}

export default function DemandSection() {
  const [demands, setDemands] = useState<Api.Demand.DemandResponse[]>([])
  const [loading, setLoading] = useState(true)
  const fetchedRef = useRef(false)

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore()

  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories().catch((err) => {
        console.error('获取业务类目失败:', err)
      })
    }
  }, [categories.length, fetchCategories])

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true

    setLoading(true)
    fetchFeaturedDemands({ limit: 3 })
      .then((res) => {
        const list = Array.isArray(res) ? res : ((res as any)?.items || [])
        setDemands(list.slice(0, 3))
      })
      .catch((err) => {
        console.error('获取企业需求主页精选列表失败:', err)
        fetchedRef.current = false
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  // 仅对 API 实际返回的需求列表进行转换
  const displayItems = demands.map((apiDemand, index) => {
    const categoryCodes = apiDemand.category_codes || []
    const categoryNames = categoryCodes
      .map((code) => getCategoryNameByCode(code, ''))
      .filter(Boolean)

    const categoryName = categoryNames[0] || (categoryCodes[0] ? getCategoryNameByCode(categoryCodes[0], '') : '')

    // tags 直接为接口返回类目 code 转换后的类目名称列表，不再使用硬编码保底数据
    const tags = categoryNames

    return {
      id: apiDemand.id || `demand-${index}`,
      title: apiDemand.title || '暂无标题',
      category: categoryName,
      description: apiDemand.description_summary || apiDemand.description || '暂无描述',
      tags: [],
      budgetDisplay: apiDemand.budget_display || '面议',
      budgetType: apiDemand.budget_type,
      avatar: logoImg, // 头像默认使用logo
      isLink: true,
    }
  })

  return (
    <section className="home-demand home-screen relative">
      <div className="home-demand__content">
        {/* 顶部标题区 */}
        <div className="demand-heading text-center">
          <h2>
            现在，社区正在<span>寻找什么？</span>
          </h2>
          <p>发现最新的 <span>AI 短剧</span> 合作与商单机会</p>
        </div>

        {/* 需求卡片与右侧控制按键外层 */}
        <div className="flex items-center gap-4 lg:gap-6 w-full">
          {/* 需求卡片网格 */}
          <div className="demand-grid flex-1 overflow-visible">
            {loading || displayItems.length === 0 ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="demand-card opacity-60 animate-pulse bg-white/70 rounded-3xl p-6 h-[280px]"
                >
                  <div className="h-6 bg-slate-200 rounded w-1/2 mb-3" />
                  <div className="h-4 bg-slate-200 rounded w-16 mb-4" />
                  <div className="h-16 bg-slate-200 rounded mb-6" />
                  <div className="h-8 bg-slate-200 rounded w-1/3 mt-auto" />
                </div>
              ))
            ) : (
              displayItems.map((item) => {
                return (
                  <Link
                    key={item.id}
                    to={item.isLink ? `/demands/${item.id}` : '/demands'}
                    className="demand-card group relative bg-gradient-to-b from-white/95 via-white/90 to-sky-50/40 backdrop-blur-xl rounded-3xl p-6 sm:p-7 border border-white/90 shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_40px_rgba(55,170,118,0.14)] transition-all duration-300 flex flex-col justify-between overflow-visible no-underline text-inherit"
                  >
                    {/* 右上角浮动光圈头像（默认使用Logo） */}
                    <div className="absolute -top-6 right-6 sm:-top-7 sm:right-7 w-16 h-16 sm:w-20 sm:h-20 rounded-full p-1 bg-gradient-to-b from-sky-100/90 via-sky-50/50 to-blue-100/30 shadow-[0_8px_20px_rgba(59,130,246,0.15)] flex items-center justify-center border border-white/80 z-10 pointer-events-none transition-transform duration-300 group-hover:scale-105">
                      <div className="w-full h-full rounded-full overflow-hidden border-2 border-white bg-white/90 shadow-inner flex items-center justify-center p-2">
                        <img
                          src={item.avatar}
                          alt="avatar"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <div>
                      {/* 卡片标题 */}
                      <div className="text-slate-800 font-bold text-xl sm:text-2xl leading-relaxed tracking-tight truncate pr-20 py-1 group-hover:text-emerald-600 transition-colors">
                        {item.title}
                      </div>

                      {/* 标题下方的分类绿色胶囊 */}
                      <div className="mt-2.5 mb-4 min-h-[1.5rem] flex items-center">
                        {item.category && (
                          <span className="inline-block bg-emerald-100/70 text-emerald-700 text-xs px-2.5 py-0.5 rounded font-medium border border-emerald-200/50">
                            {item.category}
                          </span>
                        )}
                      </div>

                      {/* 中间多行描述 */}
                      <p className="text-slate-500 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-6 max-h-[4.25rem] overflow-hidden text-justify">
                        {item.description}
                      </p>
                    </div>

                    {/* 底部元信息行 */}
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-100/60">
                      {/* 左侧标签列表 */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.tags.map((tag, tIdx) => (
                          <span
                            key={tIdx}
                            className="px-2.5 py-1 text-xs font-normal text-slate-600 bg-slate-100/80 rounded border border-slate-200/50"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* 右侧绿色预算显示 */}
                      <div>
                        {renderBudgetContent(item.budgetDisplay, item.budgetType)}
                      </div>
                    </div>
                  </Link>
                )
              })
            )}
          </div>

          {/* 右侧圆圈翻页/箭头指示按钮 */}
          <div className="hidden xl:flex flex-shrink-0">
            <Link
              to="/demands"
              className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-md shadow-lg border border-slate-100/80 flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:scale-105 transition-all"
              title="查看更多需求"
            >
              <HomeIcon name="chevron-right" size={22} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}





